import React, { useState } from "react";
import { Calendar, MapPin, User, HelpCircle, Plus } from "lucide-react";
import { MainLayout } from "./MainLayout";
import { useApp } from "./AppContext";
import { useTranslation } from "../i18n/TranslationContext";
import { formatDateDisplay } from "../utils/dateUtils";
import { ContractDownloadRequired } from "./ContractDownloadRequired";
import { ContractSignAndSend } from "./ContractSignAndSend";
import { ContractUCMProcessing } from "./ContractUCMProcessing";
import { ContractUCMReady } from "./ContractUCMReady";
import { ContractComplete } from "./ContractComplete";

export function JobDetail() {
	const { t } = useTranslation();
	const {
		selectedJob,
		setCurrentScreen,
		contractState,
		processState,
		navigateWithProcessCheck,
		userProfile,
	} = useApp();
	const [showHelpDialog, setShowHelpDialog] = useState(false);

	if (!selectedJob) {
		return (
			<MainLayout
				showBack
				onBack={() => setCurrentScreen("dashboard")}
				showBottomNav={false}
			>
				<div className="text-center py-8">
					<p className="text-[var(--ucm-text-medium)]">
						{t("jobDetail.noJobSelected")}
					</p>
				</div>
			</MainLayout>
		);
	}

	const handleBack = () => {
		setCurrentScreen("dashboard");
	};

	const handleTaskList = () => {
		// Only allow task list access for "check-in-required" status jobs
		if (selectedJob.status === "check-in-required") {
			setCurrentScreen("task-list");
		}
	};

	// Check if task list access should be allowed
	const canAccessTaskList = () => {
		return selectedJob.status === "check-in-required";
	};

	const handleAddToCalendar = () => {
		// Add to Google Calendar function
		console.log("Add to Google Calendar");
	};

	const handleHelp = () => {
		setShowHelpDialog(true);
	};

	const handleFAQClick = () => {
		setShowHelpDialog(false);
		navigateWithProcessCheck("app-info-faq");
	};

	const renderContractSection = () => {
		// Only show contract section if user has booked shifts and hasn't completed contract
		if (!processState.hasBookedShifts || processState.contractCompleted) {
			return null;
		}

		switch (contractState.status) {
			case "download_required":
				return <ContractDownloadRequired />;
			case "sign_and_send":
				return <ContractSignAndSend />;
			case "ucm_processing":
				return <ContractUCMProcessing />;
			case "ucm_ready":
				return <ContractUCMReady />;
			case "complete":
				return <ContractComplete />;
			default:
				return null;
		}
	};

	return (
		<MainLayout showBack onBack={handleBack}>
			<div className="mb-6">
				<div className="mb-2">
					<span className="text-sm font-medium text-[var(--ucm-text-medium)]">
						{t("jobDetail.id")} {selectedJob.id}
					</span>
				</div>
				<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)]">
					{selectedJob.title}
				</h1>
			</div>

			{/* Job Information */}
			<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
				<div className="space-y-4">
					<div className="flex items-center space-x-4">
						<div className="w-8 h-8 bg-[var(--ucm-light-blue)] rounded-full flex items-center justify-center">
							<Calendar size={16} className="text-[var(--ucm-dark-blue)]" />
						</div>
						<div className="flex items-center space-x-4">
							<span className="text-[var(--ucm-text-dark)]">
								{formatDateDisplay(selectedJob.date)}
							</span>
							<span className="text-[var(--ucm-text-medium)]">
								{selectedJob.time}
							</span>
						</div>
					</div>

					<div className="flex items-center space-x-4">
						<div className="w-8 h-8 bg-[var(--ucm-light-blue)] rounded-full flex items-center justify-center">
							<MapPin size={16} className="text-[var(--ucm-dark-blue)]" />
						</div>
						<span className="text-[var(--ucm-text-dark)]">
							{selectedJob.location}
						</span>
					</div>

					<div className="flex items-center space-x-4">
						<div className="w-8 h-8 bg-[var(--ucm-light-blue)] rounded-full flex items-center justify-center">
							<User size={16} className="text-[var(--ucm-dark-blue)]" />
						</div>
						<span className="text-[var(--ucm-text-dark)]">
							{userProfile
								? `${userProfile.firstName} ${userProfile.lastName}`
								: t("jobDetail.user")}
						</span>
					</div>
				</div>
			</div>

			{/* Contract Section */}
			{renderContractSection()}

			{/* Action Buttons */}
			<div className="flex items-center space-x-3 mb-6">
				{canAccessTaskList() && (
					<button
						onClick={handleTaskList}
						className="flex-1 h-12 bg-[var(--ucm-success-green)] text-white font-semibold rounded-full hover:bg-[var(--ucm-dark-green)] transition-colors duration-200"
					>
						{t("jobDetail.taskList")}
					</button>
				)}
				<button
					onClick={handleAddToCalendar}
					className="w-12 h-12 bg-white border border-[var(--ucm-border-light)] rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
				>
					<Calendar size={20} className="text-[var(--ucm-text-medium)]" />
				</button>
				<button
					onClick={handleHelp}
					className="w-12 h-12 bg-white border border-[var(--ucm-border-light)] rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
				>
					<HelpCircle size={20} className="text-[var(--ucm-text-medium)]" />
				</button>
				{/* Task List Access Info - Now positioned horizontally */}
				{!canAccessTaskList() && (
					<div className="flex-1 bg-[var(--ucm-light-blue)] border border-[var(--ucm-border-light)] rounded-2xl p-4 h-12 flex items-center">
						<p className="text-sm text-[var(--ucm-dark-blue)] text-center flex-1">
							{t("jobDetail.taskListAvailable")}
						</p>
					</div>
				)}
			</div>

			{/* Job Detail Section */}
			<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
				<h2 className="text-xl font-semibold text-[var(--ucm-text-dark)] mb-4">
					{t("jobDetail.jobDetail")}
				</h2>

				<div className="space-y-4 text-[var(--ucm-text-dark)]">
					<p>{t("jobDetail.jobDescription")}</p>

					<p>{t("jobDetail.duties")}</p>

					<ul className="list-decimal list-inside space-y-1 ml-4">
						<li>{t("jobDetail.securityCheckpoints")}</li>
						<li>{t("jobDetail.monitoringPassengerFlow")}</li>
						<li>{t("jobDetail.collaborating")}</li>
					</ul>

					<p>{t("jobDetail.reportPreparation")}</p>

					<p>{t("jobDetail.roleRequirements")}</p>
				</div>
			</div>

			{/* Provided Document Section */}
			<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
				<h2 className="text-xl font-semibold text-[var(--ucm-text-dark)] mb-4">
					{t("jobDetail.providedDocument")}
				</h2>
				<div className="space-y-4 text-[var(--ucm-text-dark)]">
					<p>
						{t("jobDetail.inspectionDocumentsDesc")}
					</p>

					<ul className="list-decimal list-inside space-y-1 ml-4">
						<li>
							<b>{t("jobDetail.safetyRegulationsManual")}:</b> {t("jobDetail.safetyRegulationsManualDesc")}
						</li>
						<li>
							<b>{t("jobDetail.inspectionChecklist")}:</b> {t("jobDetail.inspectionChecklistDesc")}
						</li>
						<li>
							<b>{t("jobDetail.incidentReportForms")}:</b> {t("jobDetail.incidentReportFormsDesc")}
						</li>
						<li>
							<b>{t("jobDetail.emergencyProceduresGuide")}:</b> {t("jobDetail.emergencyProceduresGuideDesc")}
						</li>
						<li>
							<b>{t("jobDetail.trainingMaterials")}:</b> {t("jobDetail.trainingMaterialsDesc")}
						</li>
					</ul>

					<p>
						{t("jobDetail.documentReminder")}
					</p>
				</div>
			</div>

			{/* Help Dialog */}
			{showHelpDialog && (
				<div
					className="dialog-overlay dialog-overlay-enter"
					onClick={() => setShowHelpDialog(false)}
				>
					<div
						className="dialog-card dialog-card-enter"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="text-center mb-6">
							<div className="w-12 h-12 bg-[var(--ucm-light-blue)] rounded-full flex items-center justify-center mx-auto mb-4">
								<HelpCircle size={24} className="text-[var(--ucm-dark-blue)]" />
							</div>
							<h3 className="mb-2">{t("jobDetail.needHelp")}</h3>
							<p className="text-[var(--ucm-text-medium)] mb-0">
								{t("jobDetail.howToGetAssistance")}
							</p>
						</div>

						<div className="space-y-3">
							<button
								onClick={handleFAQClick}
								className="w-full h-12 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200"
							>
								{t("jobDetail.readFAQs")}
							</button>

							<button
								onClick={() => setShowHelpDialog(false)}
								className="w-full h-10 bg-transparent text-[var(--ucm-text-medium)] rounded-full hover:bg-gray-50 transition-colors duration-200"
							>
								{t("jobDetail.cancel")}
							</button>
						</div>
					</div>
				</div>
			)}
		</MainLayout>
	);
}
